import { useState } from 'react';
import { RotateCcw } from 'lucide-react';
import ActionButton from '@/components/buttons/ActionButton.tsx';
import NoItems from '@/components/ui/NoItems.tsx';
import {
  SectionLayout,
  SectionLayoutSkeleton,
} from '@/components/ui/SectionLayout.tsx';
import {
  SettingsList,
  SettingsListRow,
} from '@/components/ui/SettingsList.tsx';
import { confirmDialogUseHook } from '@/components/ui/ConfirmDialog.useHook.ts';
import EditTemplateModal from '@/features/dashboard/messageTemplates/components/EditTemplateModal.tsx';
import useGetMessageTemplates from '@/features/dashboard/messageTemplates/hooks/useGetMessageTemplates.ts';
import useRestoreDefaultMessageTemplate from '@/features/dashboard/messageTemplates/hooks/useRestoreDefaultMessageTemplate.ts';
import {
  MessageChannel,
  MessageTemplateDto,
} from '@/features/dashboard/messageTemplates/types/MessageTemplateDto.ts';
import {
  getMessageTemplateSendTiming,
  getMessageTemplateTypeLabel,
} from '@/features/dashboard/messageTemplates/types/MessageTemplateType.utils.ts';

const channelCopy: Record<
  MessageChannel,
  { title: string; description: string; emptyTitle: string }
> = {
  [MessageChannel.EMAIL]: {
    title: 'Szablony e-mail',
    description:
      'Treści wysyłane automatycznie przy zdarzeniach w rezerwacji. Lista przychodzi z serwera — pojawiają się tu wszystkie obsługiwane typy.',
    emptyTitle: 'Brak szablonów e-mail',
  },
  [MessageChannel.SMS]: {
    title: 'Szablony SMS',
    description:
      'Krótkie powiadomienia wysyłane pod numer podany w rezerwacji. Treść liczy się do limitu znaków SMS.',
    emptyTitle: 'Brak szablonów SMS',
  },
};

const MessageTemplatesSection = ({ channel }: { channel: MessageChannel }) => {
  const { data: templates, isLoading } = useGetMessageTemplates(channel);
  const [editingTemplate, setEditingTemplate] =
    useState<MessageTemplateDto | null>(null);
  const restoreDefault = useRestoreDefaultMessageTemplate();
  const { confirmWithCallback } = confirmDialogUseHook();

  if (isLoading) return <SectionLayoutSkeleton rows={4} />;

  const copy = channelCopy[channel];

  const askToRestore = (template: MessageTemplateDto) => {
    const name = getMessageTemplateTypeLabel(template.messageTemplateType);

    confirmWithCallback({
      title: 'Przywróć domyślną treść',
      description: `Szablon „${name}” wróci do treści przygotowanej przez Campdesk. Obecna treść zostanie nadpisana.`,
      confirmText: 'Przywróć',
      loadingText: 'Przywracanie…',
      onConfirm: async () => {
        await restoreDefault.mutateAsync(template);
      },
    });
  };

  return (
    <>
      <SectionLayout title={copy.title} description={copy.description}>
        {templates?.length ? (
          <SettingsList>
            {templates.map((template) => (
              <SettingsListRow
                key={template.id ?? template.messageTemplateType}
                title={getMessageTemplateTypeLabel(
                  template.messageTemplateType,
                )}
                subtitle={getMessageTemplateSendTiming(
                  channel,
                  template.messageTemplateType,
                )}
                actions={
                  <>
                    <ActionButton
                      tone="outline"
                      size="toolbar"
                      onClick={() => setEditingTemplate(template)}
                    >
                      Zmień
                    </ActionButton>
                    <ActionButton
                      tone="danger"
                      size="toolbar"
                      iconOnly
                      title="Przywróć domyślną treść"
                      aria-label="Przywróć domyślną treść"
                      onClick={() => askToRestore(template)}
                    >
                      <RotateCcw />
                    </ActionButton>
                  </>
                }
              />
            ))}
          </SettingsList>
        ) : (
          <NoItems
            title={copy.emptyTitle}
            description="Gdy dodamy obsługę kolejnych zdarzeń, ich szablony pojawią się tutaj."
          />
        )}
      </SectionLayout>

      {editingTemplate && (
        <EditTemplateModal
          template={editingTemplate}
          onClose={() => setEditingTemplate(null)}
        />
      )}
    </>
  );
};

export default MessageTemplatesSection;
